import { FC, ReactNode, useEffect, useState } from "react";
// Redux
import { useAppDispatch } from "hooks/useStore";
import { appActions } from "store/app/appSlice";
import Statuses from "types/Statuses";
// Components
import QuizPagePreview from "./QuizPagePreview";
// MUI
import { Accordion as MuiAccordion, AccordionDetails, AccordionSummary, Typography, makeStyles } from "@material-ui/core";
// Icons
import { MdExpandMore } from "react-icons/md";
import Icon, { Icons } from "./Icon";
// i18next
import { useTranslation } from "react-i18next";

type Props = {
  children: ReactNode;
  label: string;
  icon?: Icons;
  size?: 'small' | 'default';
  defaultExpanded?: boolean;
  previewPage?: string;
  previewPageRef?: string;
  error?: any;
  hidden?: boolean;
}

const Accordion:FC<Props> = ({
  children, label, icon, size = 'default',
  defaultExpanded = false, error = false,
  previewPage, previewPageRef, hidden
}) => {
  const useStyles = makeStyles((theme) => ({
    root: {
      padding: size === 'default' ? '8px 6px' : 'none',
      marginBottom: '10px',
      backgroundColor: '#454444',
      color: 'rgba(255,255,255,0.87)',
      borderRadius: '12px',
      '&:first-child': {
        borderRadius: '12px'
      },
      '&:last-child': {
        borderRadius: '12px'
      },
      '&.Mui-expanded:last-child': {
        marginBottom: '10px',
      }
    },
    summary: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    title: {
      fontSize: size === 'default' ? '24px' : '16px',
      fontWeight: size === 'default' ? 700 : 500
    }
  }));

  const classes = useStyles();

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [open, setOpen] = useState(Boolean(defaultExpanded));
  const toggle = () => setOpen(!open);

  useEffect(() => {
    if (typeof defaultExpanded !== 'boolean') return;
    setOpen(defaultExpanded);
  }, [defaultExpanded]);

  useEffect(() => {
    if (!error) return;
    dispatch(appActions.createNotification({ message: t('validations.validationFailed'), status: Statuses.Error }));
    // eslint-disable-next-line
  }, [error]);

  if (hidden) return null;
  return (
    <MuiAccordion className={classes.root} expanded={open}>
      <AccordionSummary
        expandIcon={<MdExpandMore color="white" />}
        aria-controls={`${label}-content`}
        id={`${label}-header`}
        onClick={toggle}
      >
        <div className={classes.summary}>
          {error ? (
            <Icon 
              className="text-red-500"
              icon="error_outline"
            />
          ) : null}
          {icon ? <Icon icon={icon} size="large" /> : null}
          <Typography className={classes.title}>{label}</Typography>
          {previewPage ? (
            <QuizPagePreview page={previewPage} pageRef={previewPageRef} />
          ) : null}
        </div>
      </AccordionSummary>
      <AccordionDetails>
        {children}
      </AccordionDetails>
    </MuiAccordion>
  )
}

export default Accordion;
